import { Control, Controller } from 'react-hook-form';
import { CatalogProductsFormData } from '../../create-product-dialog-types';
import { useMemo, useState } from 'react';
import { useDebounce } from '../../../../../../../hooks/use-debounce';
import { useQuickBooksProducts } from '../../../../../../../hooks/requests/integrations';
import { isEmptyArray, isEmptyString, isNotEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import { createQuickBooksIntegrationEntity } from '../../../../../../../utils/integration-entity-creators';
import { DEBOUNCE_DELAY } from '../../../../../../../components/design/Input/input-consts';
import { Autocomplete, TextField } from '@mui/material';
import type { FormattedIntegrationProduct } from '../../../../../../../utils/types/formatted-integration-product';

type QuickBooksProductsDropdownProps = {
  control: Control<CatalogProductsFormData>;
};

export const QuickBooksProductsDropdownForCatalog: React.FC<QuickBooksProductsDropdownProps> = ({
  control,
}) => {
  const [searchInput, setSearchValue] = useState<string | undefined>(undefined);
  const debouncedSearchValue = useDebounce(searchInput, DEBOUNCE_DELAY);
  const { data: { products } = {} } = useQuickBooksProducts(debouncedSearchValue, {
    keepPreviousData: true,
  });

  const formattedProducts = useMemo((): FormattedIntegrationProduct[] => {
    if (isEmptyArray(products)) {
      return [];
    }

    return products.map(({ id, name }) => ({
      id,
      name,
    }));
  }, [products]);

  return (
    <Controller
      control={control}
      name="quickBooksProduct"
      render={({ field }) => {
        const currentValue = isNotEmptyArray(field.value) ? field.value?.at(0) : null;

        const canFindProduct = isNotNullOrUndefined(currentValue) && isNotEmptyArray(formattedProducts);

        const matchingProduct = canFindProduct
          ? formattedProducts.find((product) => product.id === currentValue.integrationExternalId)
          : null;

        return (
          <Autocomplete
            options={formattedProducts}
            multiple={false}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={matchingProduct}
            onChange={(_, value) => {
              const newEntity = createQuickBooksIntegrationEntity(value);
              field.onChange(newEntity ? [newEntity] : null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="QB Products"
                placeholder="Select QuickBooks product"
                size="small"
                slotProps={{
                  ...params.slotProps,

                  inputLabel: {
                    shrink: true,
                  }
                }}
                onChange={(e) => {
                  if (isEmptyString(e.target.value)) {
                    field.onChange(undefined);
                  } else {
                    setSearchValue(e.target.value);
                  }
                }}
              />
            )}
          />
        );
      }}
    />
  );
};
